import { Box, Button, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { FormProvider, useForm } from 'react-hook-form';
import { useGlobals } from 'src/shared/contexts/appContext';
import { toast } from 'react-toastify';
import { getAssociationFeeById, updateAssociationFee } from 'src/shared/services/associationService';
import { getOrganizationAssociationId } from 'src/shared/functions/organizationHelpers';
import ControlledRadioButton from 'src/shared/components/form/controlledRadioButton';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import { getPaymentMethodOptions, PaymentMethodEnum } from 'src/shared/models/enums/paymentMethod.enum';
import { Add } from '@mui/icons-material';
import { AssociationSendingMethodHandler } from '../components/associationSendingMethodHandler';
import { AssociationFeeProfileHandler } from '../components/associationFeeProfileHandler';
import { AssociationFeeRequestSchema } from '../schemas/associationFeeRequestSchema';
import { AssociationFeeRequest } from '../models/associationFeeRequest';
import { AssociationFee, PaymentMethodOption } from '../models/associationFee';

export const AssociationFeePage = () => {
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const params = useParams();
  const isMounted = useIsMounted();

  // Tillsvidare sätts allt till readonly
  const readOnlyMode = true;

  const currentAssocationId = getOrganizationAssociationId(currentUser, params.id);
  const paymentMethodOptions = getPaymentMethodOptions(t);

  const [association, setAssociation] = useState<AssociationFee>();
  const [loading, setLoading] = useState<boolean>(false);
  const [openAddprofile, setOpenAddprofile] = useState<boolean>(false);

  const schema = AssociationFeeRequestSchema(t);
  const formMethods = useForm<AssociationFeeRequest>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      localAss_id: Number(currentAssocationId) || 0,
      paymentMethod: 0,
      ocr: 'false',
      sameFeeForAll: 0,
      mainAndFamily: {
        feeMainMember: 0,
        feeFamilyMember: 0,
      },
      familySteps: [
        { member: 'mainMember', fee: 0 },
        { member: 'familyMember1', fee: 0 },
      ],
      oneFeeForHousehold: 0,
      feeBasedOnProfile: undefined,
    },
  });

  const { handleSubmit, reset, setValue, watch } = formMethods;

  const paymentMethod = watch('paymentMethod');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAssociationFeeById(currentAssocationId);
      if (isMounted()) {
        setAssociation(result?.data || 0);
        setValue('paymentMethod', result.data.paymentMethod);
        setValue('feeBasedOnProfile', result.data.feeBasedOnProfile);
        if (result.data.mainAndFamily !== null) setValue('mainAndFamily', result.data.mainAndFamily);
        if (result.data.familySteps !== null) setValue('familySteps', result.data.familySteps);
        setValue('sameFeeForAll', result.data.sameFeeForAll);
        setValue('oneFeeForHousehold', result.data.oneFeeForHousehold);
        setValue('ocr', result.data.ocr.toString());
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [currentAssocationId, isMounted, setValue]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selectedPaymentMethod = paymentMethodOptions.find((opt) => opt.value === watch('paymentMethod'));

  const FieldsToClean: Record<PaymentMethodEnum, Array<keyof AssociationFeeRequest>> = {
    [PaymentMethodEnum.SameFeeForAll]: ['mainAndFamily', 'familySteps', 'oneFeeForHousehold'],
    [PaymentMethodEnum.MainAndFamily]: ['sameFeeForAll', 'familySteps', 'oneFeeForHousehold'],
    [PaymentMethodEnum.FamilySteps]: ['sameFeeForAll', 'mainAndFamily', 'oneFeeForHousehold'],
    [PaymentMethodEnum.OneFeeForHousehold]: ['sameFeeForAll', 'mainAndFamily', 'familySteps'],
    [PaymentMethodEnum.FeeBasedOnProfile]: ['sameFeeForAll', 'mainAndFamily', 'familySteps', 'oneFeeForHousehold'],
    [PaymentMethodEnum.None]: ['sameFeeForAll', 'mainAndFamily', 'familySteps', 'oneFeeForHousehold'],
  };

  const onSubmit = async (data: AssociationFeeRequest) => {
    setLoading(true);

    const cleanedData: AssociationFeeRequest = {
      ...data,
      ocr: data.ocr === 'true',
    };
    if (typeof paymentMethod !== 'number') return;
    FieldsToClean[paymentMethod as PaymentMethodEnum]?.forEach((field) => {
      (cleanedData as Partial<AssociationFeeRequest>)[field] = undefined;
    });

    try {
      await updateAssociationFee(cleanedData);
      if (isMounted()) {
        reset();
        toast.success(<>{t('association:feeUpdateSuccess')}</>);
        fetchData();
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (isMounted()) {
        const errorMessage = e?.data?.messages;
        toast.error(<>{errorMessage}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <FormLoaderWrapper loading={loading}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: 2 }}>
            <Grid container>
              {association?.centralConfig && (
                <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '1.1rem' }}>
                  {t('association:centralNotice')}
                </Typography>
              )}
              {/* Diskutera vidare om denna ska visas för kund */}
              {!readOnlyMode && (
                <Grid container mt={2}>
                  <Grid item xs={12}>
                    <ControlledRadioButton
                      name='ocr'
                      disabled={readOnlyMode}
                      label={t('association:useOcr')}
                      items={[
                        {
                          value: 'true',
                          label: t('common:button.yes'),
                        },
                        {
                          value: 'false',
                          label: t('common:button.no'),
                        },
                      ]}
                    />
                  </Grid>
                </Grid>
              )}
              <Grid container mt={1} spacing={2}>
                <Grid item xs={10} md={4} sx={{ mb: { xs: -5, md: 0 } }}>
                  <AutocompleteDropdown<PaymentMethodOption>
                    options={paymentMethodOptions}
                    disable={readOnlyMode}
                    name='paymentMethod'
                    nameProp='description'
                    placeholder={t('association:paymentMethod.none')}
                    emptyLabel={t('association:paymentMethod.none')}
                    value={selectedPaymentMethod}
                    keyProp='id'
                    label='Aviseringsmetod'
                    callback={(val) => {
                      setValue('paymentMethod', val?.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={7} ml={2} sx={{ ml: { xs: 0, md: 2 }, mt: { xs: 5, md: 0 } }}>
                  <AssociationSendingMethodHandler readOnlyMode={readOnlyMode} paymentMethod={paymentMethod || 0} />
                </Grid>
              </Grid>
              <Grid item xs={11} mt={5}>
                <AssociationFeeProfileHandler readOnlyMode={readOnlyMode} openAddprofile={openAddprofile} setOpenAddprofile={setOpenAddprofile} />
              </Grid>
              {!readOnlyMode && (
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: { xs: 'space-between', sm: 'flex-end' }, mt: 2 }}>
                  <Button
                    variant='outlined'
                    size='small'
                    disableElevation
                    onClick={() => {
                      setOpenAddprofile(true);
                    }}
                  >
                    <Add fontSize='small' />
                    {t('association:addProfile')}
                  </Button>

                  <Button
                    disabled={paymentMethod === undefined || paymentMethod === 0}
                    sx={{ marginLeft: 2 }}
                    type='submit'
                    variant='contained'
                    disableElevation
                  >
                    {t('common:button.save')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        </form>
      </FormProvider>
    </FormLoaderWrapper>
  );
};
